/*
 * Copyright 2025 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useState, useCallback } from 'react';
import { devToolsApiRef } from '../api';
import { useApi } from '@backstage/core-plugin-api';

export const useScheduledTasksOperations = () => {
  const api = useApi(devToolsApiRef);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>();

  const triggerTask = useCallback(
    async (plugin: string, taskId: string) => {
      setIsLoading(true);
      setError(undefined);

      try {
        await api.triggerScheduledTask(plugin, taskId);
      } catch (e) {
        setError(e);
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [api],
  );

  const cancelTask = useCallback(
    async (plugin: string, taskId: string) => {
      setIsLoading(true);
      setError(undefined);

      try {
        await api.cancelScheduledTask(plugin, taskId);
      } catch (e) {
        setError(e);
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [api],
  );

  return {
    triggerTask,
    cancelTask,
    isLoading,
    error: error?.message,
  };
};
